import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import { useTournamentAuth } from "../../utils/authorization";
import { DangerButton, SecondaryButton } from "../UI/DesignStandards";

const DeleteTournament = ({ tournament }) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState("");
  const [isCheckingSquads, setIsCheckingSquads] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [hasActiveSquads, setHasActiveSquads] = useState(false);

  // Check tournament authorization
  const { canManage } = useTournamentAuth(tournament.id);

  useEffect(() => {
    const checkActiveSquads = async () => {
      try {
        const { data: squads, error } = await supabase
          .from("squads")
          .select("id")
          .eq("tournamentID", tournament.id)
          .eq("isDeleted", false);

        if (error) throw error;
        setHasActiveSquads(squads && squads.length > 0);
      } catch (err) {
        console.error("Error checking squads:", err);
      }
    };

    checkActiveSquads();
  }, [tournament.id]);

  const checkSquads = async () => {
    setIsCheckingSquads(true);
    try {
      const { data: squads, error } = await supabase
        .from("squads")
        .select("id, isDeleted")
        .eq("tournamentID", tournament.id)
        .eq("isDeleted", false);

      if (error) throw error;

      if (squads && squads.length > 0) {
        setError(
          "Cannot delete tournament: There are active squads. Please delete all squads first."
        );
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error checking squads:", err);
      setError("Error checking squads. Please try again.");
      return false;
    } finally {
      setIsCheckingSquads(false);
    }
  };

  const handleDelete = async () => {
    if (confirmationText !== tournament.name) {
      setError(
        "Please type the tournament name exactly as shown to confirm deletion"
      );
      return;
    }

    setIsDeleting(true);
    setError("");

    try {
      // First check if there are any active squads
      const canDelete = await checkSquads();
      if (!canDelete) {
        setIsDeleting(false);
        return;
      }

      // If no active squads, proceed with deletion
      const { error } = await supabase
        .from("tournaments")
        .update({
          isDeleted: true,
          deletedAt: new Date().toISOString(),
          deletedBy: (await supabase.auth.getUser()).data.user.id,
        })
        .eq("id", tournament.id);

      if (error) throw error;

      // Navigate back to tournaments list
      navigate("/tournaments");
    } catch (err) {
      console.error("Error deleting tournament:", err);
      setError("Error deleting tournament. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setShowDeleteForm(false);
    setConfirmationText("");
    setError("");
  };

  // Don't render anything if user doesn't have permission
  if (!canManage) {
    return null;
  }

  return (
    <div className="card-board p-6">
      <div className="mb-4">
        <h3 className="heading-display text-lg mb-3">Danger Zone</h3>
        {!showDeleteForm && (
          <div className="space-y-3">
            <DangerButton
              onClick={() => setShowDeleteForm(true)}
              disabled={hasActiveSquads}
              icon={FaTrash}
              className="w-full"
            >
              Delete Tournament
            </DangerButton>
            {hasActiveSquads && (
              <p className="text-sm text-brand-muted">
                This tournament cannot be deleted because it has active squads.
                Please delete all squads first.
              </p>
            )}
          </div>
        )}
      </div>

      {showDeleteForm && (
        <div className="space-y-4 border-t border-brand-charcoal/10 pt-4">
          <div className="rounded-lg p-4 bg-red-50">
            <p className="text-sm text-red-700">
              Warning: This action cannot be undone. All tournament data will be
              permanently deleted.
            </p>
          </div>

          <div className="card-inset p-4">
            <p className="text-sm text-brand-muted mb-2">
              To confirm deletion, please type the tournament name exactly as
              shown:
            </p>
            <p className="font-medium text-brand-charcoal mb-2">{tournament.name}</p>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type tournament name to confirm"
              className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <SecondaryButton
              onClick={handleCancel}
              className="!px-3 !py-2 text-sm"
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              onClick={handleDelete}
              disabled={isDeleting || isCheckingSquads}
            >
              {isDeleting || isCheckingSquads
                ? "Processing..."
                : "Delete Tournament"}
            </DangerButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteTournament;
